import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  inject,
  input,
  OnInit,
  Output
} from '@angular/core';
import { FiscalMonth } from '@design-system/components/m-fiscal-calendar/m-fiscal-calendar.component.types';
import { CompareService } from '@portal/app/shared/services/compare.service';
import {
  FiscalDatePresetService,
  PresetOption
} from '@portal/app/shared/services/fiscal-date-preset.service';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

@Component({
  selector: 'portal-fiscal-calendar',
  templateUrl: './portal-fiscal-calendar.component.html',
  styleUrls: ['./portal-fiscal-calendar.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PortalFiscalCalendarComponent implements OnInit {
  readonly label = input('Time Period');
  readonly startDate = input<string>('');
  readonly endDate = input<string>('');
  readonly previousStartDate = input<string | null>(null);
  readonly previousEndDate = input<string | null>(null);
  readonly months = input<FiscalMonth[]>([]);
  readonly minDate = input<dayjs.Dayjs | undefined>(undefined);
  readonly maxDate = input<dayjs.Dayjs | undefined>(undefined);
  readonly relativeDate = input<string | null>(null);
  readonly showOnlyCalendarPopup = input<boolean>(false);
  readonly datePickerInputHeight = input<string>('2rem');

  @Output() readonly dateChanged = new EventEmitter<{
    startDate: string;
    endDate: string;
    previousStartDate: string | null;
    previousEndDate: string | null;
    presetValue: string;
  }>();

  showCalendar = false;
  forceCompareOn: boolean | undefined = undefined;
  compareToggledOn = false;
  compareEnabled = true;

  private appliedDatePreset = 'custom';
  private datePresetOptions: readonly PresetOption[] = [];
  private dateToShowSingleRange = '';
  private dateToShowCompareRange = '';

  private readonly displayFormat = 'MMM D, YYYY';
  private readonly compareService = inject(CompareService);
  private readonly fiscalDatePresetService = inject(FiscalDatePresetService);

  ngOnInit(): void {
    this.compareToggledOn = this.compareService.compare;
    this.compareEnabled = this.compareService.compareIsAvailable;
    if (!this.compareEnabled) {
      this.compareToggledOn = false;
    }
    this.datePresetOptions = [
      ...this.fiscalDatePresetService.datePresetOptions
    ].sort((a, b) => a.order - b.order);

    const savedPreset = this.relativeDate();
    if (savedPreset && savedPreset !== 'custom_range') {
      const match = this.datePresetOptions.find((o) => o.value === savedPreset);
      if (match) {
        this.appliedDatePreset = savedPreset;
      }
    }

    this.updateLabelsFromInputs();
  }

  toggleCalendar(event?: MouseEvent): void {
    event?.preventDefault();
    this.showCalendar ? this.closeCalendar() : this.openCalendar();
  }

  onOutsideCompareToggle(value: boolean): void {
    this.forceCompareOn = value;
    this.compareToggledOn = value;
    this.openCalendar();
  }

  getDatePickerLabel(): string {
    if (this.appliedDatePreset !== 'custom') {
      const option = this.datePresetOptions.find(
        (o) => o.value === this.appliedDatePreset
      );
      if (option) return option.label;
    }


    const cached = this.compareToggledOn
      ? this.dateToShowCompareRange
      : this.dateToShowSingleRange;
    if (cached) return cached;


    const start = this.startDate();
    const end = this.endDate();
    if (start && end) {
      const s = dayjs(start).utc(true).format(this.displayFormat);
      const e = dayjs(end).utc(true).format(this.displayFormat);
      return `${s} - ${e}`;
    }
    return '';
  }

  onPopupDateChanged(event: {
    startDate: string;
    endDate: string;
    previousStartDate: string | null;
    previousEndDate: string | null;
    presetValue: string;
  }): void {
    this.appliedDatePreset = event.presetValue;
    this.compareToggledOn = this.compareService.compare;
    this.updateLabelsFromDates(
      event.startDate,
      event.endDate,
      event.previousStartDate,
      event.previousEndDate
    );
    this.closeCalendar();
    this.dateChanged.emit(event);
  }

  onPopupCancelled(): void {
    this.compareToggledOn = this.compareService.compare;
    this.forceCompareOn = undefined;
    this.closeCalendar();
  }

  private openCalendar(): void {
    if (this.showCalendar) return;
    this.showCalendar = true;
  }

  private closeCalendar(): void {
    this.showCalendar = false;
    this.forceCompareOn = undefined;
  }

  private updateLabelsFromInputs(): void {
    const start = this.startDate();
    const end = this.endDate();
    if (start && end) {
      const s = dayjs(start).utc(true).format(this.displayFormat);
      const e = dayjs(end).utc(true).format(this.displayFormat);
      this.dateToShowSingleRange = `${s} - ${e}`;

      const ps = this.previousStartDate();
      const pe = this.previousEndDate();
      if (ps && pe) {
        const psFmt = dayjs(ps).utc(true).format(this.displayFormat);
        const peFmt = dayjs(pe).utc(true).format(this.displayFormat);
        this.dateToShowCompareRange = `${s} - ${e} | ${psFmt} - ${peFmt}`;
      } else {
        this.dateToShowCompareRange = this.dateToShowSingleRange;
      }
    }
  }

  private updateLabelsFromDates(
    startDate: string,
    endDate: string,
    previousStartDate: string | null,
    previousEndDate: string | null
  ): void {
    const s = dayjs(startDate).utc(true).format(this.displayFormat);
    const e = dayjs(endDate).utc(true).format(this.displayFormat);
    this.dateToShowSingleRange = `${s} - ${e}`;

    if (previousStartDate && previousEndDate) {
      const ps = dayjs(previousStartDate).utc(true).format(this.displayFormat);
      const pe = dayjs(previousEndDate).utc(true).format(this.displayFormat);
      this.dateToShowCompareRange = `${s} - ${e} | ${ps} - ${pe}`;
    } else {
      this.dateToShowCompareRange = this.dateToShowSingleRange;
    }
  }
}
